import { setTag } from '@sentry/react';
import { Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  styled,
  Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { VyIcon } from '@weft/icons';
import { withDefault } from '@weft/utils';
import cn from 'classnames';
import React, { useEffect } from 'react';
import { useAccount } from '../../../../../hooks/requests/use-account';
import { useActivate } from '../../../../../hooks/use-activate';
import { ArrowIcon } from '../../../../icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../../../icons/ArrowIcon/types/arrow-icon-direction';
import { NAVIGATION_LABELS } from '../../navbar-types/navigation-label';
import { useHighlightColor } from '../../side-navbar-hooks/use-highlight-color';
import { InnerListItemButtonLink } from '../InnerListItemButtonLink';
import { ListItemButtonLink } from '../ListItemButtonLink';

const invoiceSubMenus = [
  { href: '/invoices/overview', label: 'Invoices' },
  { href: '/invoices/monthly-closure', label: 'Monthly Closure' },
  { href: '/invoices/payments', label: 'Payments' },
];

const usageSubMenus = [
  { href: '/usage/metering', label: 'Usage' },
  { href: '/usage/events', label: 'Events' },
];

const StyledTypography = styled(Typography)({
  color: blueGrey['300'],
});

const StyledListItemButton = styled(ListItemButton)({
  color: blueGrey['300'],
  '&:hover': {
    backgroundColor: blueGrey['900'],
  },
});

type NavbarListProps = {
  showFullNav: boolean;
};

export const NavbarList: React.FC<NavbarListProps> = ({ showFullNav }) => {
  const { data, isLoading: isLoadingAccount } = useAccount({
    onSuccess: ({ account }) => {
      setTag('user.account_name', account.name);
    },
  });
  const account = data?.account;

  const getHighlightColor = useHighlightColor();

  const [isInvoicesMenuOpen, openInvoicesMenu, closeInvoicesMenu] = useActivate(location.pathname.startsWith('/invoices'));
  const [isMeteringMenuOpen, openMeteringMenu, closeMeteringMenu] = useActivate(location.pathname.startsWith('/usage'));

  useEffect(() => {
    if (!showFullNav) {
      closeInvoicesMenu();
      closeMeteringMenu();
    }
  }, [closeInvoicesMenu, closeMeteringMenu, showFullNav]);

  return (
    <List
      component="nav"
      sx={{
        width: '100%',
      }}
      subheader={(
        <ListSubheader
          sx={theme => ({
            backgroundColor: theme.palette.common.black,
            marginBottom: '16px',
            paddingLeft: !showFullNav ? '12px' : '',
          })}
        >
          <StyledTypography
            variant="body2"
          >
            {!showFullNav ? 'Co.' : 'Company'}
          </StyledTypography>
          <Typography
            variant="h6"
            sx={theme => ({
              color: theme.palette.primary.contrastText,
            })}
          >
            {isLoadingAccount ?
              <Skeleton
                sx={{ backgroundColor: blueGrey['700'] }}
                variant="text"
              />
              : !showFullNav ? `${withDefault(account?.name, '').slice(0, 2)}...` : account?.name}
          </Typography>
        </ListSubheader>
      )}
    >
      <ListItemButtonLink to="/home">
        <ListItemIcon>
          <VyIcon
            icon="Layout"
            color={getHighlightColor('/home')}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1', color: getHighlightColor('/home') }}>
          {NAVIGATION_LABELS.HOME}
        </ListItemText>
      </ListItemButtonLink>

      <ListItemButtonLink to="/customers">
        <ListItemIcon>
          <VyIcon
            icon="Group"
            color={getHighlightColor('/customers')}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1', color: getHighlightColor('/customers') }}>
          {NAVIGATION_LABELS.CUSTOMERS}
        </ListItemText>
      </ListItemButtonLink>

      <ListItemButtonLink to="/plans">
        <ListItemIcon>
          <VyIcon
            icon="DocumentText"
            color={getHighlightColor('/plans')}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1', color: getHighlightColor('/plans') }}>
          {NAVIGATION_LABELS.PLANS}
        </ListItemText>
      </ListItemButtonLink>

      <StyledListItemButton
        onClick={() => isInvoicesMenuOpen ? closeInvoicesMenu() : openInvoicesMenu()}
        sx={{ color: getHighlightColor('/invoices', true) }}
      >
        <ListItemIcon>
          <VyIcon
            icon="InvoiceListItem"
            color={getHighlightColor('/invoices', true)}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
          Invoices
        </ListItemText>

        <ArrowIcon
          color={getHighlightColor('/invoices', true)}
          className={cn('transition', { 'rotate-180': isInvoicesMenuOpen })}
          direction={ARROW_ICON_DIRECTIONS.DOWN}
        />
      </StyledListItemButton>

      <Collapse
        in={isInvoicesMenuOpen}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >
          {invoiceSubMenus.map(({ label, href }) => (
            <InnerListItemButtonLink
              key={href}
              to={href}
            >
              <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
                {label}
              </ListItemText>
            </InnerListItemButtonLink>
          ))}
        </List>
      </Collapse>

      <StyledListItemButton
        onClick={() => isMeteringMenuOpen ? closeMeteringMenu() : openMeteringMenu()}
        sx={{ color: getHighlightColor('/usage') }}
      >
        <ListItemIcon>
          <VyIcon
            icon="ChartStyleBig6"
            color={getHighlightColor('/usage')}
          />
        </ListItemIcon>

        <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
          {NAVIGATION_LABELS.DATA_METERING}
        </ListItemText>

        <ArrowIcon
          color={getHighlightColor('/usage')}
          className={cn('transition', { 'rotate-180': isMeteringMenuOpen })}
          direction={ARROW_ICON_DIRECTIONS.DOWN}
        />
      </StyledListItemButton>

      <Collapse
        in={isMeteringMenuOpen}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >
          {usageSubMenus.map(({ label, href }) => (
            <InnerListItemButtonLink
              key={href}
              to={href}
            >
              <ListItemText primaryTypographyProps={{ variant: 'body1' }}>
                {label}
              </ListItemText>
            </InnerListItemButtonLink>
          ))}
        </List>
      </Collapse>

      <ListItemButtonLink to="/reports">
        <ListItemIcon>
          <VyIcon
            icon="InvoiceAccountingDocumentChart"
            color={getHighlightColor('/reports')}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1', color: getHighlightColor('/reports') }}>
          {NAVIGATION_LABELS.REPORTS}
        </ListItemText>
      </ListItemButtonLink>

      <ListItemButtonLink to="/products">
        <ListItemIcon>
          <VyIcon
            icon="PackageBoxOpen"
            color={getHighlightColor('/products')}
          />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ variant: 'body1', color: getHighlightColor('/products') }}>
          {NAVIGATION_LABELS.PRODUCTS}
        </ListItemText>
      </ListItemButtonLink>
    </List>
  );
};